import { cloneDeep, isEmpty, isNil } from 'lodash';
import {
  CSSProperties,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextSubtitle } from '@shared/components/typography';
import { Field } from '@shared/data/entity/fields/Field';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import {
  useViewTemplate,
  useWorkflowTemplate,
  useWorkspaceDetails,
} from '@shared/scenes/workflows/hooks';
import { getComponentIcon, isAuditTagComponent } from '@shared/scenes/workflows/utils';
import { SubformView } from '@shared/scenes/workflows/views/view-components/SubformView';
import { WorkflowView } from '@shared/scenes/workflows/views/view-components/WorkflowView';
import { ColorThemeContext, Colors, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { MBAlert } from '@shared/util/alert';
import { NO_CODE_SUBFORMS, useFeatureFlag } from '@shared/util/featureFlags';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import {
  MBNavStackProvider,
  SubformRoute,
  useBuilderLink,
  useNavStackContext,
} from '@shared/util/navigation';
import { CardFormTypeContext } from '../../../cardFormTypeContext';
import { FieldRecord } from '../../../types';
import { applyComponentOverrides } from '../../../util/useViewComponents';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { EditFieldContainer } from '../EditFieldContainer';
import { SubformContent } from './SubformsContent';
import { SubformsSheet } from './SubformsSheet';
import { countCompletedSubformFields } from './hooks';
import { useSubformData } from './useSubformData';

// Provides per-component-id subform validation errors to SubformEditField instances.
interface SubformErrorContextValue {
  getSubformError: (componentId: string) => string | undefined;
  clearSubformError: (componentId: string) => void;
}

const SubformErrorContext = createContext<SubformErrorContextValue>({
  getSubformError: () => undefined,
  clearSubformError: () => undefined,
});

interface Props {
  field: SubformField | null;
  component: API.SubformComponent;
  workflowTemplateId: string;
  updateField: (componentId: string, field: SubformField | null, force?: boolean) => void;
  error?: string;
  allowCreate?: boolean;
  style?: CSSProperties;
}

export function SubformEditField({
  field,
  component,
  workflowTemplateId,
  updateField,
  error,
  allowCreate = true,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const [isSheetOpen, openSheet, closeSheet] = useBoolean(false);
  const workspaceDetails = useWorkspaceDetails();
  const hasSubform = !isNil(field?.subform);
  const nav = useNavStackContext<SubformRoute>();
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const subformTemplateResult = useWorkflowTemplate(field?.subform?.workflowTemplateId);
  const subformTemplate = subformTemplateResult.workflowTemplate;
  const isReadOnly = component.readonly ?? false;
  const channelSlug = workspaceDetails.get(workflowTemplateId)?.baseNavigationPath;
  const navigateToBuilder = useBuilderLink(workflowTemplateId, channelSlug);
  const anchorName = getAnchorName('subform-edit-field', component.id);

  const { getSubformError, clearSubformError } = useContext(SubformErrorContext);
  const subformContextError = getSubformError(component.id);
  const effectiveError = error ?? subformContextError;
  const formType = useContext(CardFormTypeContext);
  const isNoCodeSubforms = useFeatureFlag(NO_CODE_SUBFORMS);

  // Load the view template for the selected subform so validate() can check
  // required overrides without requiring the user to open the modal first.
  const subformViewTemplateId =
    formType === 'CREATE'
      ? subformTemplate?.viewTemplateDefaults?.create
      : subformTemplate?.viewTemplateDefaults?.update;

  const { viewTemplate: subformViewTemplate, loading: subformViewTemplateLoading } =
    useViewTemplate(field?.subform?.workflowTemplateId ?? null, subformViewTemplateId ?? null);

  // Sync workflowTemplate, view-template overrides, and audit-tag defaults into the
  // SubformField so that hasUnfilledRequiredFields() works and child defaults are pre-seeded.
  // This also fires for subforms populated via a config default (not just user-selected ones),
  // fixing two gaps: defaults not prefilled and required-field validation not enforced.
  useEffect(() => {
    if (!isNoCodeSubforms) {
      return;
    }
    if (isNil(field?.subform) || isNil(subformTemplate)) {
      return;
    }
    // Wait only while the view template is actively loading. If the query finishes but returns
    // null (e.g. a 403 in external forms), proceed without the full template — the
    // viewTemplateId string is already known from the workflow template's viewTemplateDefaults
    // and will be stamped onto the subform data directly.
    if (!isNil(subformViewTemplateId) && subformViewTemplateLoading) {
      return;
    }

    const unseededDefaults = getUnseededAuditTagDefaults(subformTemplate, field.subform.data);
    if (isSubformFieldSynced(field, subformViewTemplate, subformViewTemplateId, unseededDefaults)) {
      return;
    }

    // skipEqualityCheck bypasses isFieldModified deduplication, which only compares subform
    // JSON data and would otherwise swallow this update since components (with overrides) is
    // not part of isEqual.
    updateField(
      component.id,
      buildSyncedSubformField(
        field,
        subformTemplate,
        subformViewTemplate,
        unseededDefaults,
        subformViewTemplateId,
      ),
      /* skipEqualityCheck */ true,
    );
  }, [
    isNoCodeSubforms,
    subformViewTemplate,
    subformViewTemplateLoading,
    subformViewTemplateId,
    field,
    subformTemplate,
    component.id,
    updateField,
  ]);

  const onSelectSubform = useCallback(
    (subformWorkflowTemplate: API.WorkflowTemplateFragment) => {
      const viewTemplateId = isNoCodeSubforms
        ? formType === 'CREATE'
          ? subformWorkflowTemplate.viewTemplateDefaults?.create
          : subformWorkflowTemplate.viewTemplateDefaults?.update
        : undefined;

      const componentsWithDefaults =
        subformWorkflowTemplate.components
          ?.filter(isAuditTagComponent)
          .filter(c => !isEmpty(c.default?.value)) ?? [];

      const defaultData = componentsWithDefaults.reduce((acc, subformComponent) => {
        acc[subformComponent.id] = { tags: subformComponent.default?.value };
        return acc;
      }, {});

      updateField(
        component.id,
        new SubformField(
          { workflowTemplateId: subformWorkflowTemplate.id, viewTemplateId, data: defaultData },
          null,
          { workflowTemplate: subformWorkflowTemplate },
        ),
      );
      clearSubformError(component.id);
      closeSheet();
    },
    [component.id, updateField, closeSheet, formType, isNoCodeSubforms, clearSubformError],
  );

  const navigateToAdd = useCallback(() => {
    navigateToBuilder({ route: '/subform', isNested: true, includeMenu: false });
  }, [navigateToBuilder]);

  const onSelect = useCallback(
    (subformId: string) => {
      nav.push({
        name: 'subform',
        params: {
          workflowTemplateId: subformId,
          parentComponentId: component.id,
          isReadOnly,
        },
      });
      closeSheet();
    },
    [component.id, nav, closeSheet, isReadOnly],
  );

  const onClear = useCallback(() => {
    MBAlert.confirm({
      message: i18n('card.blocks.subformsInput.confirmClear'),
      confirmText: i18n('common.ok'),
      onConfirm: () => {
        updateField(component.id, null);
        clearSubformError(component.id);
      },
    });
  }, [component.id, updateField, clearSubformError]);

  const subformComponents = subformTemplate?.components ?? [];
  const completedFields = countCompletedSubformFields(
    subformComponents,
    field?.subform?.data ?? {},
  );
  const printContent = subformTemplate
    ? `${subformTemplate.name}\n${i18n('card.blocks.subformsInput.fieldsCompleted', {
        count: completedFields,
      })}`
    : undefined;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showLabel={true}
      indentLabel={true}
      showIcon={false}
      largerInput={false}
      error={effectiveError}
      style={style}
      printContent={printContent}
    >
      <MBTouchableOpacity
        onPress={hasSubform || isReadOnly ? undefined : openSheet}
        style={{
          marginHorizontal: responsiveForms ? undefined : -grid(1),
          paddingVertical: responsiveForms ? undefined : grid(0.5),
        }}
      >
        {hasSubform && (
          <SubformContainer
            {...{
              field,
              onSelect,
              onClear,
              isReadOnly,
            }}
          />
        )}
        {!hasSubform && <EmptyContainer {...{ component }} />}
        {isSheetOpen && (
          <SubformsSheet
            anchorName={anchorName}
            blockLabel={component.label ?? ''}
            workflowTemplate={workflowTemplate}
            onClose={closeSheet}
            onSelect={onSelectSubform}
            navigateToAdd={allowCreate ? navigateToAdd : undefined}
          />
        )}
      </MBTouchableOpacity>
    </EditFieldContainer>
  );
}

function SubformContainer({
  field,
  onSelect,
  onClear,
  isReadOnly = false,
}: {
  field: SubformField | null;
  onSelect: (subformId: string) => void;
  onClear: VoidFunction;
  isReadOnly?: boolean;
}) {
  const responsiveForms = useResponsiveFormContext();
  if (isNil(field?.subform)) {
    return null;
  }
  return (
    <RNView style={[styles.subformContainer, responsiveForms && styles.responsiveSubformContainer]}>
      <SubformContent
        subform={field.subform}
        onSelect={onSelect}
        onClear={onClear}
        isReadOnly={isReadOnly}
      />
    </RNView>
  );
}

function EmptyContainer({ component }: { component: API.SubformComponent }) {
  const responsiveForms = useResponsiveFormContext();
  const colors = useContext(ColorThemeContext);
  const containerColors = {
    backgroundColor: colors.backgroundInactive,
    borderColor: colors.input.borderInactive,
  };
  const responsiveFormStyle = {
    backgroundColor: Colors.v2.transparent,
    borderColor: Colors.v2.transparent,
  };

  return (
    <View paddingHorizontal={responsiveForms ? 0 : 1}>
      <MBView
        style={[styles.container, containerColors, responsiveForms && responsiveFormStyle]}
        row
        centerVertically
        centerHorizontally
        paddingLeft={1}
        paddingRight={2}
      >
        <View marginRight={1}>
          <MBIconV2
            color={colors.foregroundActive}
            size={grid(5)}
            name={getComponentIcon(component) ?? 'list'}
          />
        </View>
        <MBTextSubtitle
          numberOfLines={1}
          ellipsizeMode="tail"
          color={colors.foregroundActive}
          style={{ flexGrow: 0, flexShrink: 1 }}
        >
          {component.placeholder}
        </MBTextSubtitle>
      </MBView>
    </View>
  );
}

interface SubformNavigationContextProps {
  fields: FieldRecord | null;
  updateField: (componentId: string, field: Field | null, skipEqualityCheck?: boolean) => void;
  children: ReactNode;
  /** True once the parent form's save has been attempted while errors exist.
   *  SubformNavigationContext uses this to drive per-subform required-error display. */
  saveAttempted?: boolean;
  /** Server-side errors from the parent form. Used to surface subform errors returned by
   *  the API (e.g. COMPONENT_DATA_REQUIRED) when local view-template validation cannot
   *  catch them. */
  apiErrors?: CardEntityError[];
}

export function SubformNavigationContext(props: SubformNavigationContextProps) {
  return (
    <MBNavStackProvider<SubformRoute> initialRoutes={[{ name: 'cardForm' }]}>
      <ConditionalNav {...props} />
    </MBNavStackProvider>
  );
}

function ConditionalNav({
  fields,
  updateField,
  children,
  saveAttempted = false,
  apiErrors,
}: SubformNavigationContextProps) {
  const navContext = useNavStackContext<SubformRoute>();
  const isSubformRoute = navContext.currentRoute?.name === 'subform' && !isNil(fields);
  const formType = useContext(CardFormTypeContext);

  const workflowTemplateId = navContext.currentRoute?.params?.workflowTemplateId ?? '';
  const parentComponentId = navContext.currentRoute?.params?.parentComponentId ?? '';
  const isReadOnly = navContext.currentRoute?.params?.isReadOnly ?? false;

  const field = fields?.[parentComponentId] as SubformField;
  const initialData = useMemo(
    () => (isNil(field?.subform) ? {} : cloneDeep(field.subform.data)),
    // cloneDeep is stable; re-clone only when the subform reference changes
    [field?.subform],
  );

  // Derive inner-subform API errors from the server's COMPONENT_DATA_REQUIRED response.
  // The server's subformComponentId identifies which inner field is missing data; we
  // remap it to componentId so useSubformData → useFormErrors → SubformView can display
  // it on the correct field via getFieldError.
  const subformApiErrors = useMemo(() => {
    if (!isSubformRoute) {
      return undefined;
    }
    const innerErrors = (apiErrors ?? [])
      .filter(e => e.componentId === parentComponentId && !isNil(e.subformComponentId))
      .map(e => ({ description: e.description, componentId: e.subformComponentId }));
    return innerErrors.length > 0 ? innerErrors : undefined;
  }, [apiErrors, parentComponentId, isSubformRoute]);

  const subformData = useSubformData({
    workflowTemplateId,
    initialData,
    type: formType,
    apiErrors: subformApiErrors,
  });
  const {
    viewTemplate: subformViewTemplate,
    workflowTemplate: subformWorkflowTemplate,
    setSubmitAttempted,
  } = subformData;

  const isNoCodeSubforms = useFeatureFlag(NO_CODE_SUBFORMS);

  // Track which component IDs have been "revealed" as having subform errors — either
  // because the parent form save was attempted while they had errors, or because the
  // subform modal was dismissed without fixing required fields.
  const [erroredIds, setErroredIds] = useState<Set<string>>(new Set());

  // Tracks component IDs that have server-side subform errors (COMPONENT_DATA_REQUIRED).
  // Stored separately so the setSubmitAttempted effect can bypass the
  // hasUnfilledRequiredFields() guard when the view template is missing.
  const [serverErroredIds, setServerErroredIds] = useState<Set<string>>(new Set());

  // Seed erroredIds and serverErroredIds from incoming API errors that carry a componentId.
  // This surfaces server-side COMPONENT_DATA_REQUIRED errors on the parent form's subform
  // fields and prepares the inner subform to show required errors when opened.
  useEffect(() => {
    if (!isNoCodeSubforms) {
      return;
    }
    const ids = new Set(
      (apiErrors ?? []).map(e => e.componentId).filter((id): id is string => !isNil(id)),
    );
    setServerErroredIds(ids);
    if (ids.size > 0) {
      setErroredIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });
    }
  }, [apiErrors, isNoCodeSubforms]);

  // Stable ref so the save-attempt effect can read the latest fields without listing
  // fields as a dependency (which would re-seed errors whenever VT options reload).
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  // When the parent form save is attempted with subform errors, seed erroredIds.
  // Depends only on saveAttempted — not fields — so VT-option reloads don't re-seed.
  useEffect(() => {
    if (!saveAttempted || !isNoCodeSubforms) {
      return;
    }
    const currentFields = fieldsRef.current;
    setErroredIds(prev => {
      const next = new Set(prev);
      for (const [id, f] of Object.entries(currentFields ?? {})) {
        if (f instanceof SubformField && f.hasUnfilledRequiredFields()) {
          next.add(id);
        }
      }
      return next;
    });
  }, [saveAttempted, isNoCodeSubforms]);

  // Errors clear reactively: getSubformError only returns a message when the field
  // still has unfilled required fields, so fixing or replacing the subform auto-clears.
  const getSubformError = useCallback(
    (id: string) => {
      if (!erroredIds.has(id)) {
        return undefined;
      }
      return i18n('card.blocks.subformsInput.hasRequiredErrors');
    },
    [erroredIds],
  );

  const clearSubformError = useCallback(
    (id: string) =>
      setErroredIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }),
    [],
  );

  // When the subform modal is opened while it has a known error, surface required
  // errors inside the modal immediately. For server-side errors the
  // hasUnfilledRequiredFields() guard is bypassed because the view template may be
  // absent and local validation cannot detect the missing field.
  useEffect(() => {
    if (!isSubformRoute || !isNoCodeSubforms) {
      return;
    }
    const f = fields?.[parentComponentId];
    if (
      erroredIds.has(parentComponentId) &&
      f instanceof SubformField &&
      (f.hasUnfilledRequiredFields() || serverErroredIds.has(parentComponentId))
    ) {
      setSubmitAttempted(true);
    }
  }, [
    isSubformRoute,
    isNoCodeSubforms,
    parentComponentId,
    erroredIds,
    serverErroredIds,
    fields,
    setSubmitAttempted,
  ]);

  const errorContextValue = useMemo(
    () => ({ getSubformError, clearSubformError }),
    [getSubformError, clearSubformError],
  );

  const onSubmit = useCallback(
    ({
      data,
      wasValid,
    }: {
      data: Record<string, SupportedSubformComponentValue | null>;
      wasValid: boolean;
    }) => {
      // Fall back to the field's existing viewTemplateId when the view template hasn't
      // loaded (e.g. it's missing on the server). Without this, closing the subform modal
      // while useSubformViewTemplate returns undefined would overwrite the stored
      // viewTemplateId with undefined, stripping it from subsequent saves.
      const existingViewTemplateId = (
        fieldsRef.current?.[parentComponentId] as SubformField | null | undefined
      )?.subform?.viewTemplateId;
      updateField(
        parentComponentId,
        new SubformField(
          {
            workflowTemplateId,
            viewTemplateId: subformViewTemplate?.id ?? existingViewTemplateId,
            data,
          },
          null,
          {
            workflowTemplate: subformWorkflowTemplate,
            components: subformWorkflowTemplate
              ? applyComponentOverrides(subformWorkflowTemplate.components, subformViewTemplate)
              : undefined,
          },
        ),
        /* skipEqualityCheck */ true,
      );
      if (wasValid) {
        clearSubformError(parentComponentId);
      } else {
        setErroredIds(prev => new Set(prev).add(parentComponentId));
      }
      navContext.pop();
    },
    [
      updateField,
      parentComponentId,
      workflowTemplateId,
      subformViewTemplate,
      subformWorkflowTemplate,
      clearSubformError,
      navContext,
    ],
  );

  const onSubmitOld = useCallback(
    (data: Record<string, SupportedSubformComponentValue>) => {
      updateField(
        parentComponentId,
        new SubformField(
          { workflowTemplateId, data },
          null,
          subformWorkflowTemplate ? { workflowTemplate: subformWorkflowTemplate } : undefined,
        ),
      );
      clearSubformError(parentComponentId);
      navContext.pop();
    },
    [
      parentComponentId,
      updateField,
      workflowTemplateId,
      subformWorkflowTemplate,
      clearSubformError,
      navContext,
    ],
  );

  return (
    <SubformErrorContext.Provider value={errorContextValue}>
      {children}
      {isSubformRoute && isNoCodeSubforms && (
        <SubformView {...subformData} onSubmit={onSubmit} isReadOnly={isReadOnly} />
      )}
      {isSubformRoute && !isNoCodeSubforms && (
        <WorkflowView
          workflowTemplateId={workflowTemplateId}
          initialData={initialData}
          onSubmit={onSubmitOld}
          isReadOnly={isReadOnly}
        />
      )}
    </SubformErrorContext.Provider>
  );
}

/**
 * Returns the subset of audit-tag components in a subform template whose default values
 * exist but have not yet been written into the given data record.
 */
function getUnseededAuditTagDefaults(
  subformTemplate: API.WorkflowTemplateFragment,
  data: Record<string, any>,
) {
  return (
    subformTemplate.components
      ?.filter(isAuditTagComponent)
      .filter(c => !isEmpty(c.default?.value) && !(c.id in data)) ?? []
  );
}

/**
 * Returns true when a SubformField already has its workflowTemplate, view template, and
 * all audit-tag defaults fully synced — i.e. no update is needed.
 *
 * When the view template could not be loaded (e.g. a 403 in external forms),
 * `subformViewTemplate` will be nil even though `subformViewTemplateId` is known.
 * In that case we consider the view-template step done if the ID is already stamped on
 * the field — component overrides cannot be applied without the full template object.
 */
function isSubformFieldSynced(
  field: SubformField,
  subformViewTemplate: API.ViewTemplateFragment | null | undefined,
  subformViewTemplateId: string | null | undefined,
  unseededDefaults: ReturnType<typeof getUnseededAuditTagDefaults>,
): boolean {
  const workflowTemplateSynced = !isNil(field.workflowTemplate);
  let viewTemplateSynced: boolean;
  if (isNil(subformViewTemplateId)) {
    viewTemplateSynced = true;
  } else if (!isNil(subformViewTemplate)) {
    viewTemplateSynced =
      !isNil(field.components) && field.subform.viewTemplateId === subformViewTemplate.id;
  } else {
    // View template expected but unavailable (e.g. 403): ID is the best we can do.
    viewTemplateSynced = field.subform.viewTemplateId === subformViewTemplateId;
  }
  return workflowTemplateSynced && viewTemplateSynced && isEmpty(unseededDefaults);
}

/**
 * Builds an updated SubformField with workflowTemplate set, optional view-template overrides
 * applied, and any unseeded audit-tag defaults merged into data.
 *
 * `subformViewTemplateId` is used as a fallback when the full view template object is
 * unavailable (e.g. a 403 in external forms) so that the viewTemplateId is still stamped
 * onto the subform data even without component override information.
 */
function buildSyncedSubformField(
  field: SubformField,
  subformTemplate: API.WorkflowTemplateFragment,
  subformViewTemplate: API.ViewTemplateFragment | null | undefined,
  unseededDefaults: ReturnType<typeof getUnseededAuditTagDefaults>,
  subformViewTemplateId?: string | null,
): SubformField {
  const seededData = unseededDefaults.reduce(
    (acc, c) => ({ ...acc, [c.id]: { tags: c.default?.value } }),
    { ...field.subform.data },
  );
  const resolvedViewTemplateId = subformViewTemplate?.id ?? subformViewTemplateId;
  return new SubformField(
    {
      ...field.subform,
      data: seededData,
      ...(!isNil(resolvedViewTemplateId) ? { viewTemplateId: resolvedViewTemplateId } : {}),
    },
    null,
    {
      workflowTemplate: subformTemplate,
      ...(!isNil(subformViewTemplate)
        ? { components: applyComponentOverrides(subformTemplate.components, subformViewTemplate) }
        : {}),
    },
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: grid(1),
    borderStyle: 'dashed',
    borderWidth: 2,
    paddingVertical: grid(2),
    alignItems: 'center',
    justifyContent: 'center',
  },
  subformContainer: {
    paddingHorizontal: grid(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  responsiveSubformContainer: {
    paddingHorizontal: 0,
  },
});
